/**
 * The boundary between what staff may change about a workspace and what belongs
 * to the workspace — in words a person can read, keyed to the two constants in
 * `@wend/shared` that enforce it.
 *
 * WHY IT IS RENDERED AT ALL. A disabled field with no explanation reads as a
 * bug. Staff meet a greyed-out subscription key, conclude the admin panel is
 * broken, and the next step is a support engineer running an `UPDATE` by hand —
 * which is precisely the unattributable write the whole slice exists to stop.
 * A sentence saying *why* the field is not editable, and what the operation
 * would be instead, costs one line and removes the reason to route around it.
 *
 * WHY IT IS TYPED AGAINST THE CONSTANTS. Both maps are
 * `Record<TenantFieldAdminMayNotChange, …>` and
 * `Record<TenantDataAdminMayNotReach, …>`, so adding a name to either constant
 * in `@wend/shared` fails this app's typecheck until somebody writes the
 * sentence for it. The alternative — an array of hand-written rows next to the
 * constants — drifts silently, and the way it drifts is that the screen keeps
 * confidently listing a boundary the code no longer draws. That is worse than
 * no list, because a stale claim about what an admin cannot reach is exactly the
 * claim nobody re-checks.
 *
 * WHAT THIS FILE IS NOT. It is copy, not enforcement. `.strict()` on
 * `tenantEditableFieldsSchema` refuses a body naming `subscriptionKey`, and the
 * absence of any admin route over contacts or templates is what makes the second
 * list true. Nothing here can make either of those less true, and nothing here
 * should ever be the only place a boundary is stated.
 */
import type { TenantDataAdminMayNotReach, TenantFieldAdminMayNotChange } from '@wend/shared';

export interface UneditableFieldCopy {
  /** How the field is named to a person, not how the column is named. */
  readonly label: string;
  /** Why it cannot be changed here, and what would change it instead. */
  readonly why: string;
}

/**
 * One entry per name in `TENANT_FIELDS_ADMIN_MAY_NOT_CHANGE`.
 *
 * `subscriptionKey` carries the longest sentence on purpose: it is the only one
 * of the five somebody has a plausible reason to want to edit, so it is the only
 * one where "no" needs an argument rather than an assertion.
 */
export const UNEDITABLE_FIELD_COPY: Readonly<
  Record<TenantFieldAdminMayNotChange, UneditableFieldCopy>
> = {
  id: {
    label: 'Workspace ID',
    why: 'Wend’s own identifier for this workspace. Every campaign, contact and ledger entry points at it; changing it would orphan all of them.',
  },
  subscriptionKey: {
    label: 'Subscription key',
    why: 'Unique across every workspace, and the value support asks a customer to read back. Editing it here would invalidate the one the customer holds — and, if it collided with another workspace’s, would surface as a database constraint failure rather than as a message about this field. Rotating a key is a different operation with a different blast radius: it needs its own confirmation and its own audit action, and it will get them when somebody needs one.',
  },
  createdByAdminId: {
    label: 'Created by',
    why: 'The staff member who provisioned this workspace. Provenance that can be edited is not provenance.',
  },
  createdAt: {
    label: 'Created',
    why: 'When the workspace was provisioned. Set once, by the row being written.',
  },
  updatedAt: {
    label: 'Last changed',
    why: 'Moved by the database on every write, including the ones made from this screen.',
  },
};

/**
 * One entry per name in `TENANT_DATA_ADMIN_MAY_NOT_REACH` — the tenant's own
 * data, which no admin route reads or writes.
 *
 * The panel that renders this list is not a feature request. It is the answer to
 * a question staff will otherwise ask support: "can I just look at their
 * contacts to check?" The honest answer is no, and the reason is that
 * `wend_admin` is `BYPASSRLS` — it *could*, which is exactly why the boundary is
 * drawn in routes that do not exist rather than in a policy the database would
 * have enforced for us.
 */
export const UNREACHABLE_DATA_LABEL: Readonly<Record<TenantDataAdminMayNotReach, string>> = {
  contacts: 'Contacts',
  datasets: 'Uploaded datasets',
  segments: 'Segments',
  templates: 'Templates',
  campaigns: 'Campaigns',
  messages: 'Messages and delivery history',
  integrations: 'Amazon SES and Meta credentials',
  suppressions: 'Suppression list',
};
